import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, SealCheck, MapPin, Star } from "@phosphor-icons/react";
import { COMPANY, IMAGES, SERVICES, COMMUNES, RGE_SERVICES } from "../data/site";
import { CtaButtons, RgeBadge } from "../components/Cta";
import { Reveal, stagger, item } from "../components/Reveal";
import { Blob, Dot, Ring } from "../components/Decor";
import TrustBanner from "../components/TrustBanner";
import CtaBanner from "../components/CtaBanner";
import ServiceIcon from "../components/ServiceIcon";

const easeOut = [0.22, 1, 0.36, 1];
const HERO_LINES = ["Chauffage & énergies", "renouvelables — Saintois", "& agglo. de Nancy."];
const WHY = [
  ["01", "Artisan RGE local", "Certifiés RGE Qualit'EnR, nous garantissons un travail conforme aux exigences de l'État.", "#FF3123"],
  ["02", "Écoute & conseil", "Un accompagnement sur mesure et des conseils clairs, sans engagement.", "#FC8529"],
  ["03", "Multi-énergies", "Chauffage, pompe à chaleur, bois, solaire, climatisation, plomberie : une seule entreprise.", "#3CC0E7"],
  ["04", "Dépannage réactif", "Entretien et interventions rapides pour préserver votre confort.", "#1A49F7"],
];

function HeroLine({ children, delay }) {
  return (
    <span className="reveal-line">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, delay, ease: easeOut }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden px-6 pb-10 pt-14 md:px-12 md:pb-16 md:pt-20">
        <Blob color="blue" className="-right-20 top-0 md:right-10" size={480} opacity={0.1} />
        <Blob color="orange" className="-left-24 bottom-0" size={360} opacity={0.1} />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <RgeBadge />
            </motion.div>
            <h1 className="mt-7 text-5xl font-black leading-[0.92] tracking-tighter text-[#14183A] md:text-7xl lg:text-[5.5rem]">
              {HERO_LINES.map((l, i) => (
                <HeroLine key={i} delay={0.15 + i * 0.12}>
                  {i === 1 ? (
                    <>renouvelables — <span className="text-[#1A49F7]">Saintois</span></>
                  ) : (
                    l
                  )}
                </HeroLine>
              ))}
            </h1>
            <motion.p
              className="mt-8 max-w-xl text-lg font-light leading-relaxed text-[#5b6072] md:text-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {COMPANY.baseline}
            </motion.p>
            <motion.div
              className="mt-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.72 }}
            >
              <CtaButtons size="lg" stack />
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              className="relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-[#F3F6FF]"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3, ease: easeOut }}
            >
              <motion.img
                src={IMAGES.heroHeatpump}
                alt="Pompe à chaleur moderne installée par Lisse Energies dans le Saintois"
                style={{ y: imgY, scale: imgScale }}
                className="h-full w-full object-cover"
              />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-2xl bg-white/90 px-5 py-4 backdrop-blur-md">
                <div className="flex items-center gap-2">
                  <Star weight="fill" className="h-5 w-5 text-[#FC8529]" />
                  <span className="text-sm font-semibold text-[#14183A]">{COMPANY.rating} · {COMPANY.reviewsCount} avis</span>
                </div>
                <span className="text-xs font-semibold uppercase tracking-widest text-[#9aa0b4]">Google</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE removed on request */}

      <TrustBanner />

      {/* SERVICES */}
      <section data-testid="services-preview" className="relative overflow-hidden px-6 py-24 md:px-12 md:py-36">
        <Blob color="cyan" className="-right-32 top-40" size={420} opacity={0.08} />
        <div className="relative mx-auto max-w-7xl">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <Reveal>
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#FF3123]">
                <Dot color="red" size={8} /> Nos prestations
              </p>
              <h2 className="mt-4 max-w-2xl text-4xl font-bold tracking-tight text-[#14183A] md:text-5xl lg:text-6xl">
                Une expertise multi-énergies, du conseil au dépannage.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/prestations" data-testid="services-view-all" className="group inline-flex items-center gap-2 text-sm font-semibold text-[#1A49F7]">
                Voir toutes les prestations
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {SERVICES.map((s) => (
              <motion.div key={s.id} variants={item}>
                <Link
                  to="/prestations"
                  data-testid={`service-card-${s.id}`}
                  className="group flex h-full flex-col rounded-[1.75rem] border border-[#EEF0F6] bg-white p-8 transition-all duration-500 hover:border-transparent hover:shadow-[0_24px_50px_-20px_rgba(20,24,58,0.15)] md:p-10"
                >
                  <div className="flex items-start justify-between">
                    <ServiceIcon name={s.icon} weight="light" className="h-11 w-11 text-[#14183A] transition-colors duration-300 group-hover:text-[#1A49F7]" />
                    <ArrowUpRight className="h-5 w-5 text-[#C7CBDA] transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#14183A]" />
                  </div>
                  <h3 className="mt-8 text-2xl font-semibold tracking-tight text-[#14183A]">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-[#5b6072]">{s.desc}</p>
                  {s.rge && (
                    <span className="mt-6 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#FFF6EF] px-3 py-1 text-[11px] font-semibold text-[#B45309]">
                      <SealCheck weight="fill" className="h-3.5 w-3.5" /> Éligible aides RGE
                    </span>
                  )}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* INTERVENTION ZONE */}
      <section data-testid="zone-section" className="relative overflow-hidden border-y border-[#E7ECFB] bg-[#F3F6FF] px-6 py-24 md:px-12 md:py-32">
        <Blob color="blue" className="right-10 top-10" size={360} opacity={0.12} />
        <Ring color="cyan" className="left-8 top-16 hidden md:block" size={90} />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#1A49F7]">
              <Dot color="blue" size={8} /> Zone d'intervention
            </p>
            <h2 className="mt-4 text-4xl font-bold tracking-tight text-[#14183A] md:text-5xl lg:text-6xl">
              Nous intervenons dans <span className="text-[#1A49F7]">tout le Saintois</span> et l'agglomération de Nancy.
            </h2>
            <p className="mt-6 max-w-lg text-lg font-light leading-relaxed text-[#5b6072]">
              Artisan de proximité basé à Praye (54), nous nous déplaçons dans le Saintois, à Nancy et alentours pour vos installations, entretiens et dépannages.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="flex flex-wrap gap-3">
              {COMMUNES.map((c) => (
                <span key={c} className="inline-flex items-center gap-2 rounded-full border border-[#DCE3F7] bg-white px-4 py-2 text-sm font-medium text-[#14183A]">
                  <MapPin weight="fill" className="h-3.5 w-3.5 text-[#3CC0E7]" /> {c}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section data-testid="why-section" className="relative overflow-hidden px-6 py-24 md:px-12 md:py-36">
        <Blob color="orange" className="-left-28 top-24" size={380} opacity={0.08} />
        <div className="relative mx-auto max-w-7xl">
          <Reveal>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#FC8529]">
              <Dot color="orange" size={8} /> Pourquoi nous choisir
            </p>
            <h2 className="mt-4 max-w-3xl text-4xl font-bold tracking-tight text-[#14183A] md:text-5xl lg:text-6xl">
              Un artisan RGE local, à vos côtés à chaque étape.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-4">
            {WHY.map(([num, title, desc, color], i) => (
              <Reveal key={num} delay={i * 0.08}>
                <div className="border-t-2 pt-6" style={{ borderColor: color }}>
                  <span className="text-sm font-bold" style={{ color }}>{num}</span>
                  <h3 className="mt-4 text-xl font-semibold tracking-tight text-[#14183A]">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#5b6072]">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REALISATIONS PREVIEW */}
      <section data-testid="realisations-preview" className="relative overflow-hidden border-y border-[#F3E9DE] bg-[#FFF6EF] px-6 py-24 md:px-12 md:py-32">
        <Blob color="orange" className="right-0 top-10" size={340} opacity={0.12} />
        <div className="relative mx-auto max-w-7xl">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <Reveal>
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#FC8529]">
                <Dot color="orange" size={8} /> Réalisations
              </p>
              <h2 className="mt-4 text-4xl font-bold tracking-tight text-[#14183A] md:text-5xl lg:text-6xl">Nos derniers chantiers.</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/realisations" data-testid="realisations-view-all" className="group inline-flex items-center gap-2 text-sm font-semibold text-[#1A49F7]">
                Voir la galerie <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { src: IMAGES.poeleFoyerBois1, caption: "Installation de poêle à bois" },
              { src: IMAGES.pompeAChaleurMitsubishi, caption: "Pose d'une pompe à chaleur" },
              { src: IMAGES.chaudiereBoisGranulesPerge, caption: "Chaufferie bois/granulés" },
            ].map((r, i) => (
              <Reveal key={i} delay={i * 0.08} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <Link to="/realisations" className="group block overflow-hidden rounded-[1.75rem] bg-white shadow-[0_10px_30px_-18px_rgba(20,24,58,0.25)]">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={r.src} alt={r.caption} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <p className="px-6 py-5 text-sm font-medium text-[#14183A]">{r.caption}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AIDES FINANCIERES */}
      <section data-testid="aides-section" className="px-6 py-16 md:px-12 md:py-24">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] border border-[#E7ECFB] bg-[#F3F6FF] px-8 py-14 md:px-16 md:py-20">
          <Blob color="blue" className="-right-16 -top-16" size={300} opacity={0.14} />
          <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
            <Reveal className="lg:col-span-8">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm">
                <SealCheck weight="light" className="h-8 w-8 text-[#FC8529]" />
              </span>
              <h2 className="mt-6 text-3xl font-bold tracking-tight text-[#14183A] md:text-4xl lg:text-5xl">
                Financez votre projet avec <span className="text-[#1A49F7]">MaPrimeRénov'</span> &amp; les CEE.
              </h2>
              <p className="mt-6 max-w-2xl text-lg font-light leading-relaxed text-[#5b6072]">
                Grâce à notre certification RGE, profitez de MaPrimeRénov' et des Certificats d'Économie d'Énergie (CEE) pour financer votre installation de {RGE_SERVICES}.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-4">
              <CtaButtons stack />
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
